import { SearchDisplayAction } from './search-display-action.tsx'
import { searchDisplayIntent } from '../lib/search-display-intent.ts'
import { questionFigure } from '../lib/search-question-figure.ts'
import { searchManageAlerts } from '../lib/search-manage-alerts.ts'
import { useSearchDateRange } from '../hooks/use-search-date-range.ts'
import { scopedSearchResult } from '../lib/search-report-scope.ts'
import { defaultStringifySearch } from '@tanstack/react-router'
import { Command } from 'cmdk'
import { Fragment, useEffect, useMemo, useRef, useState, type JSX } from 'react'

import { Dialog } from '../design-system/dialog.tsx'
import { EmptyState } from '../design-system/empty-state.tsx'
import { Icon } from '../design-system/icon.tsx'
import { Panel } from '../design-system/panel.tsx'
import { useMerchants } from '../hooks/use-merchants.ts'
import { useSearchAsk } from '../hooks/use-search-ask.ts'
import { cn } from '../lib/cn.ts'
import { figureHref } from '../lib/figure-focus.ts'
import { keywordRank, recordSearchOpened } from '../lib/search-opened.ts'
import {
  findQueryMerchant,
  isMerchantCommandValue,
  merchantCommandValue,
  merchantReportHref,
  searchMerchants
} from '../lib/merchant-search.ts'
import { merchantLabel } from '../lib/merchants.ts'
import {
  ACTION,
  ASK_AI,
  MERCHANT,
  SEE_ALL,
  suggestedScore,
  tiedPageScore
} from '../lib/palette-bands.ts'
import {
  actionCommandValue,
  isActionCommandValue,
  matchingPaletteActions,
  type PaletteAction,
  type PaletteActionId
} from '../lib/palette-action.ts'
import {
  figuresFor,
  isApplePlatform,
  isPageSearchShortcut,
  matchingFigure,
  pageSearchValue,
  type SearchPage
} from '../lib/page-search.ts'
import type { InternalPage } from '../lib/pages.ts'
import { useSearchAlerts } from '../hooks/use-page-alert.ts'
import { searchAlertIntent } from '../lib/search-alert-intent.ts'
import {
  MIN_ASK_LENGTH,
  normalizeAskQuery,
  suggestedPages
} from '../lib/search-ask.ts'

/** One page search may offer. The same shape home search ranks. */
export type PalettePage = SearchPage

/** The report on screen, when its grammar reads `orgIds`. */
export interface PaletteMerchantReport {
  path: string
  search: string
}

export interface PagePaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The pages this viewer may open. The caller applies viewer access. */
  pages: readonly PalettePage[]
  /** Open the page. The palette closes itself. */
  onNavigate: (path: string) => void
  /**
   * Whether the shortcut may open it. Off while viewer access is loading or
   * failed, when the rail hides search and the list would be empty.
   */
  enabled?: boolean
  /**
   * Merchant 360's path when this viewer may open it. Null hides that action.
   * One id in `orgIds` is the merchant that screen shows.
   */
  merchant360Path?: string | null
  /**
   * The current report when filtering it to one merchant keeps `orgIds`.
   * Null hides that action.
   */
  report?: PaletteMerchantReport | null
  /**
   * Commands this reader may run. Shown only while the query is not empty,
   * under the pages and above the merchants.
   */
  actions?: readonly PaletteAction[]
  /** Run the selected command. The palette closes itself. */
  onAction: (id: PaletteActionId) => void
  /**
   * Starred pages this viewer may open, in star order.
   * Shown above the categories while the query is empty. An empty list is hidden.
   */
  starredPages?: readonly InternalPage[]
  /**
   * Recent pages this viewer may open, newest first.
   * Shown after Starred while the query is empty. An empty list is hidden.
   */
  recentPages?: readonly InternalPage[]
}

const MANAGE_ALERTS_VALUE = 'manage-alerts\nrules'
const SEE_ALL_VALUE = 'see-all\nresults'
const ALERT_NONE_VALUE = 'alert-none\nrules'
const ASK_AI_VALUE = 'ask-ai\nquestion'

/** No commands, shared so a palette with none keeps one identity across renders. */
const NO_ACTIONS: readonly PaletteAction[] = []

/** No stored pages, shared so an omitted list keeps one identity across renders. */
const NO_STORED_PAGES: readonly InternalPage[] = []

const ITEM_CLASS = cn(
  'flex cursor-pointer flex-col gap-0.5 px-2 py-2 text-left text-sm',
  'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground'
)

const GROUP_CLASS =
  '[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium'

/**
 * Rank keyword pages above suggestions, actions, merchants, and the full search link.
 *
 * cmdk sorts ungrouped rows by this score. An action value and a merchant
 * value are not page text, so `pageSearchScore` would hide them. The smallest
 * page score is an id typo (0.05). An action row stays under that, and a
 * merchant row stays under the action. Suggestions stay between pages and actions.
 * A `Command.Group` breaks this order: cmdk 1.1.1 moves ungrouped rows after
 * the group, and its group reorder looks up
 * an id it never writes on the group. Page ties use source order.
 * The see-all row stays under every merchant.
 */
function paletteFilter(
  value: string,
  search: string,
  pageScores: ReadonlyMap<string, number>,
  suggestedIndices: ReadonlyMap<string, number>
): number {
  if (value.startsWith('display\n')) return ACTION
  if (value === MANAGE_ALERTS_VALUE) return 1
  if (value === ASK_AI_VALUE) return ASK_AI
  const rank = suggestedIndices.get(value)
  if (rank !== undefined) return suggestedScore(rank)
  if (value === SEE_ALL_VALUE) return SEE_ALL
  if (value === ALERT_NONE_VALUE) return (MERCHANT + SEE_ALL) / 2
  if (isMerchantCommandValue(value)) return MERCHANT
  if (isActionCommandValue(value)) return ACTION
  return pageScores.get(value) ?? (search.trim() === '' ? 1 : 0)
}

/** A group name as a `data-testid` fragment. "CS Tools" becomes "cs-tools". */
function slugifyGroup(group: string): string {
  return group.trim().toLowerCase().replace(/\s+/g, '-')
}

/**
 * Keep each rail section together, in the order the pages arrive.
 *
 * @param pages - The pages search may offer.
 * @returns Those pages grouped by the section name the rail shows.
 */
function grouped(
  pages: readonly PalettePage[]
): { group: string; pages: PalettePage[] }[] {
  const groups: { group: string; pages: PalettePage[] }[] = []
  const byGroup = new Map<string, PalettePage[]>()

  for (const entry of pages) {
    const existing = byGroup.get(entry.group)
    if (existing) {
      existing.push(entry)
      continue
    }
    const created = [entry]
    byGroup.set(entry.group, created)
    groups.push({ group: entry.group, pages: created })
  }

  return groups
}

/**
 * Page search.
 *
 * ⌘K opens it on an Apple platform, and Ctrl+K everywhere else. The rail's
 * search control opens it too. Each word matches a search field.
 * Results rank across sections while a query is present.
 * Enter opens that page and closes the list. Typing never asks the AI: the
 * reader picks the Ask AI row, so a half-typed word never reaches it.
 * Escape closes it.
 *
 * @param props - Page palette props.
 * @returns The dialog, mounted whether or not it is open, so the shortcut stays live.
 */
export function PagePalette({
  open,
  onOpenChange,
  pages,
  onNavigate,
  enabled = true,
  merchant360Path = null,
  report = null,
  actions = NO_ACTIONS,
  onAction,
  starredPages = NO_STORED_PAGES,
  recentPages = NO_STORED_PAGES
}: PagePaletteProps): JSX.Element {
  const actionRan = useRef(false)
  useEffect(() => {
    if (!enabled) return undefined
    function onKeyDown(event: KeyboardEvent): void {
      if (!isPageSearchShortcut(event, isApplePlatform())) return
      event.preventDefault()
      onOpenChange(!open)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled, onOpenChange, open])

  return (
    <Dialog
      data-testid="page-palette"
      title="Search pages"
      open={open}
      onOpenChange={onOpenChange}
      onCloseAutoFocus={event => {
        if (actionRan.current) event.preventDefault()
        actionRan.current = false
      }}
    >
      <PaletteContents
        pages={pages}
        onNavigate={onNavigate}
        onOpenChange={onOpenChange}
        merchant360Path={merchant360Path}
        report={report}
        actions={actions}
        onAction={id => {
          actionRan.current = true
          onAction(id)
        }}
        starredPages={starredPages}
        recentPages={recentPages}
      />
    </Dialog>
  )
}

function PaletteContents({
  pages,
  onNavigate,
  onOpenChange,
  merchant360Path,
  report,
  actions,
  onAction,
  starredPages,
  recentPages
}: Pick<
  PagePaletteProps,
  | 'pages'
  | 'onNavigate'
  | 'onOpenChange'
  | 'merchant360Path'
  | 'report'
  | 'actions'
  | 'onAction'
  | 'starredPages'
  | 'recentPages'
>): JSX.Element {
  const [search, setSearch] = useState('')
  const [askedSearch, setAskedSearch] = useState<string | null>(null)
  const [selected, setSelected] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const [selectionTouched, setSelectionTouched] = useState(false)
  const management = searchManageAlerts(search, pages)
  const displayIntent = searchDisplayIntent(search)
  const intent = searchAlertIntent(displayIntent?.topic ?? search)
  const date = useSearchDateRange(intent.topic)
  const topic = date.topic.trim()
  const emptyTopic = intent.alert && topic === ''
  const alerts = useSearchAlerts()
  const alertReports = alerts?.reports
  const alertPaths = useMemo(
    () => new Set(alertReports?.map(alertReport => alertReport.path)),
    [alertReports]
  )
  const searching = search.trim() !== ''
  const questionPage = !searching
    ? pages.find(entry => figuresFor(entry).length > 0)
    : undefined
  const alertPage = !searching
    ? pages.find(
        entry => alerts?.catalogueLoaded && alertPaths.has(entry.page.path)
      )
    : undefined
  // Opening the palette leaves this disabled until the reader types.
  const directory = useMerchants({ enabled: searching && management === null })
  const directoryMerchants = directory.isSuccess
    ? directory.data.merchants
    : undefined
  const queryMerchant = useMemo(
    () =>
      typeof merchant360Path === 'string' && directoryMerchants !== undefined
        ? findQueryMerchant(directoryMerchants, topic, pages)
        : null,
    [merchant360Path, directoryMerchants, topic, pages]
  )
  const pageQuery = queryMerchant?.rest || topic
  const asked = askedSearch === search
  const ask = useSearchAsk(management || !asked ? '' : topic)
  const offerAsk =
    searching &&
    !asked &&
    management === null &&
    !emptyTopic &&
    normalizeAskQuery(topic).length >= MIN_ASK_LENGTH
  const pageValues = useMemo(
    () =>
      pages.map(entry =>
        pageSearchValue(entry.page, entry.group, figuresFor(entry))
      ),
    [pages]
  )
  const pageScores = useMemo(
    () =>
      new Map(
        pageValues.map((value, index) => [
          value,
          tiedPageScore(value, pageQuery, index)
        ])
      ),
    [pageValues, pageQuery]
  )
  const suggestions = suggestedPages(
    emptyTopic || management ? undefined : ask.answer,
    pages
  ).filter(
    entry =>
      (pageScores.get(
        pageSearchValue(entry.page, entry.group, figuresFor(entry))
      ) ?? 0) === 0
  )
  const suggestedIndices = new Map(
    suggestions.map((entry, index) => [`suggested\n${entry.page.id}`, index])
  )
  const matches =
    searching && !management
      ? searchMerchants(directory.data?.merchants ?? [], topic)
      : []
  const showMerchants =
    matches.length > 0 &&
    (typeof merchant360Path === 'string' ||
      (report !== undefined && report !== null))
  // An empty query scores every action as a match. Search shows them only
  // once the reader has typed, under the pages.
  const visibleActions =
    searching && !management
      ? matchingPaletteActions(actions ?? [], search)
      : []
  // The see-all row is always present while searching, so cmdk's empty state
  // never shows. Count pages, actions, and merchants ourselves.
  const noPageMatches =
    emptyTopic || [...pageScores.values()].every(score => score === 0)
  const alertNonePage =
    intent.alert &&
    topic !== '' &&
    (ask.status === 'ready' ||
      ask.status === 'hidden' ||
      ask.answer?.status === 'off') &&
    alerts?.catalogueLoaded &&
    !pages.some(
      (entry, index) =>
        alertPaths.has(entry.page.path) &&
        (pageScores.get(pageValues[index] ?? '') ?? 0) > 0
    ) &&
    !suggestions.some(entry => alertPaths.has(entry.page.path))
      ? pages.find(entry => entry.page.id === 'alerts-rules')?.page
      : undefined
  // The fallback arrives after Jev answers, when cmdk has already picked the
  // see-all row. Move to the fallback unless the reader chose a row.
  const alertNoneShown = alertNonePage !== undefined
  const [alertNoneWasShown, setAlertNoneWasShown] = useState(false)
  if (alertNoneShown !== alertNoneWasShown) {
    setAlertNoneWasShown(alertNoneShown)
    if (alertNoneShown && selected === SEE_ALL_VALUE && !selectionTouched) {
      setSelected(ALERT_NONE_VALUE)
    }
  }
  const showEmpty = searching
    ? noPageMatches &&
      suggestions.length === 0 &&
      visibleActions.length === 0 &&
      !showMerchants &&
      management === null &&
      alertNonePage === undefined
    : pages.length === 0

  let leadId: string | null = null
  let leadScore = 0
  if (searching) {
    for (const [index, entry] of pages.entries()) {
      const score = pageScores.get(pageValues[index] ?? '') ?? 0
      if (score > leadScore) {
        leadScore = score
        leadId = entry.page.id
      }
    }
  }

  function tryQuestion(kind: 'question' | 'alert', text: string): JSX.Element {
    return (
      <Command.Item
        data-testid={`page-palette-try-${kind}`}
        value={`try\n${kind}`}
        onSelect={() => {
          setSearch(text)
          setAskedSearch(text)
          setSelected('')
          input.current?.focus()
        }}
        className={ITEM_CLASS}
      >
        {text}
      </Command.Item>
    )
  }

  // Only an answered ask has a record to write the opened page onto.
  function recordOpened(entry: PalettePage, suggested: boolean): void {
    if (ask.status !== 'ready') return
    // A suggestion that also matches a keyword shows as a keyword row, so the AI's own order decides.
    const aiRank =
      suggestedPages(ask.answer, pages).findIndex(
        candidate => candidate.page.id === entry.page.id
      ) + 1
    const rank =
      aiRank > 0
        ? aiRank
        : suggested
          ? null
          : keywordRank(
              pageScores,
              pageSearchValue(entry.page, entry.group, figuresFor(entry))
            )
    if (rank === null) return
    recordSearchOpened({
      query: topic,
      pageId: entry.page.id,
      rank,
      source: aiRank > 0 ? 'jev' : 'keyword'
    })
  }

  function item(
    entry: PalettePage,
    caption: boolean,
    suggested = false
  ): JSX.Element {
    const { page, group } = entry
    const alertRow = intent.alert && alertPaths.has(page.path)
    const scope = scopedSearchResult(
      page,
      date,
      page.takesMerchants ? queryMerchant?.merchant : null
    )
    const label = alertRow ? `Set up an alert · ${page.title}` : page.title
    const figures = figuresFor(entry)
    const figure = searching
      ? suggested
        ? questionFigure(figures, pageQuery)
        : matchingFigure(figures, pageQuery)
      : null
    const merchant = page.takesMerchants ? queryMerchant?.merchant : null
    return (
      <Fragment
        key={`${suggested ? 'suggested' : 'page'}-${page.id}-${queryMerchant?.merchant.orgId ?? ''}`}
      >
        <Command.Item
          // cmdk rescores registered items when the directory resolves a scope.
          key={`${suggested ? 'suggested' : 'page'}-${page.id}-${queryMerchant?.merchant.orgId ?? ''}`}
          data-testid={`page-palette-${suggested ? 'suggested' : 'item'}-${page.id}`}
          // The caption is hidden from a screen reader, so the name says it.
          aria-label={
            suggested
              ? merchant
                ? `${label}, for ${merchantLabel(merchant)}, suggested`
                : `${label}, suggested`
              : alertRow
                ? merchant
                  ? `${label}, for ${merchantLabel(merchant)}`
                  : label
                : undefined
          }
          aria-describedby={
            alertRow
              ? `palette-${suggested ? 'suggested' : 'item'}-${page.id}-alert-hint`
              : undefined
          }
          value={
            suggested
              ? `suggested\n${page.id}`
              : pageSearchValue(page, group, figures)
          }
          onSelect={() => {
            onNavigate(figureHref(scope.href, figure))
            if (alertRow) alerts?.openAlertOn(page.path)
            onOpenChange(false)
            recordOpened(entry, suggested)
          }}
          className={ITEM_CLASS}
        >
          {caption ? (
            <small
              data-testid={
                suggested ? 'page-palette-suggested' : 'page-palette-pages'
              }
              // The row's name is the page. Read aloud, the caption would join it.
              aria-hidden="true"
              className="text-muted-foreground text-xs font-medium"
            >
              {suggested ? 'Suggested' : 'Pages'}
            </small>
          ) : null}
          <div className="flex min-w-0 items-center gap-2">
            {alertRow ? (
              <Icon
                name="bell"
                data-testid={`page-palette-${suggested ? 'suggested' : 'item'}-${page.id}-alert-icon`}
                className="size-4 shrink-0"
              />
            ) : null}
            <span className="block min-w-0 truncate font-medium">{label}</span>
            {searching && figure !== null && (
              <small
                data-testid={`page-palette-figure-${page.id}`}
                className="text-muted-foreground shrink-0 text-xs"
              >
                {figure}
              </small>
            )}
            {searching && figure === null && (
              <small
                data-testid={`page-palette-section-${page.id}`}
                className="text-muted-foreground shrink-0 text-xs"
              >
                {group}
              </small>
            )}
          </div>
          {page.takesDateRange && date.range ? (
            <span className="text-muted-foreground text-xs">{scope.label}</span>
          ) : null}
          {alertRow ? (
            <span
              id={`palette-${suggested ? 'suggested' : 'item'}-${page.id}-alert-hint`}
              data-testid={`page-palette-${suggested ? 'suggested' : 'item'}-${page.id}-alert-hint`}
              className="text-muted-foreground text-xs"
            >
              Opens the alert setup on this report
            </span>
          ) : null}
          {merchant ? (
            <span
              data-testid={`page-palette-merchant-${page.id}`}
              className="text-muted-foreground text-xs"
            >
              for {merchantLabel(merchant)}
            </span>
          ) : null}
          <span className="text-muted-foreground block truncate text-xs">
            {page.description}
          </span>
        </Command.Item>
        {displayIntent &&
        (suggested ||
          (pageScores.get(pageSearchValue(page, group, figures)) ?? 0) > 0) ? (
          <SearchDisplayAction
            query={search}
            entry={entry}
            pages={pages}
            mode="palette"
            onNavigate={href => {
              onNavigate(href)
              onOpenChange(false)
            }}
          />
        ) : null}
      </Fragment>
    )
  }

  function storedRow(
    band: 'starred' | 'recent',
    page: InternalPage
  ): JSX.Element {
    return (
      <Command.Item
        key={`${band}-${page.id}`}
        data-testid={`page-palette-${band}-${page.id}`}
        // A category row uses the page's search value. The same page here
        // needs its own value, or cmdk selects both rows as one.
        value={`${band}\n${page.id}`}
        onSelect={() => {
          onNavigate(page.path)
          onOpenChange(false)
        }}
        className={ITEM_CLASS}
      >
        <span className="block min-w-0 truncate font-medium">{page.title}</span>
        <span className="text-muted-foreground block truncate text-xs">
          {page.description}
        </span>
      </Command.Item>
    )
  }

  function storedGroup(
    band: 'starred' | 'recent',
    heading: string,
    stored: readonly InternalPage[]
  ): JSX.Element | null {
    if (stored.length === 0) return null
    return (
      <Command.Group
        heading={heading}
        data-testid={`page-palette-group-${band}`}
        className={GROUP_CLASS}
      >
        {stored.map(page => storedRow(band, page))}
      </Command.Group>
    )
  }

  return (
    <Panel
      data-testid="page-palette-panel"
      title="Search pages"
      description="Find a page by its name, keywords, a figure, section, or description."
    >
      <Command
        data-testid="page-palette-command"
        value={selected}
        onValueChange={setSelected}
        onKeyDownCapture={event => {
          if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key))
            setSelectionTouched(true)
        }}
        onPointerMoveCapture={event => {
          if (
            event.target instanceof Element &&
            event.target.closest('[cmdk-item]')
          )
            setSelectionTouched(true)
        }}
        label="Ask a question or search pages"
        // The panel lays its content out in a grid, and a grid item does not
        // shrink below its content. Without this, a description's unbroken
        // width widens the list past the dialog instead of truncating.
        className="min-w-0"
        // cmdk moves the highlight on Ctrl+K. That chord is this shortcut.
        vimBindings={false}
        // cmdk 1.1.1 sorts items by score inside each group. Search uses
        // ungrouped items so the score orders results across sections.
        shouldFilter
        filter={value =>
          paletteFilter(value, pageQuery, pageScores, suggestedIndices)
        }
      >
        <div className="flex items-center gap-2 border-b px-3">
          <Icon name="search" className="text-muted-foreground size-4" />
          <Command.Input
            ref={input}
            data-testid="page-palette-search"
            value={search}
            onValueChange={next => {
              setSelectionTouched(false)
              setSearch(next)
              // Typing restores cmdk's first match; arriving suggestions keep the controlled selection.
              setSelected('')
            }}
            placeholder="Ask a question, search pages, or say “alert me when…”"
            className="placeholder:text-muted-foreground h-9 w-full bg-transparent text-sm outline-none"
          />
          {ask.status === 'loading' ? (
            <small
              data-testid="page-palette-asking"
              aria-live="polite"
              className="text-muted-foreground shrink-0 text-xs"
            >
              Finding suggestions…
            </small>
          ) : null}
        </div>
        <Command.List className="max-h-80 overflow-y-auto p-1">
          {management ? (
            <Command.Item
              data-testid="page-palette-manage-alerts"
              value={MANAGE_ALERTS_VALUE}
              onSelect={() => {
                onNavigate(management.href)
                onOpenChange(false)
              }}
              className={ITEM_CLASS}
            >
              <span className="font-medium">{management.label}</span>
              <span className="text-muted-foreground text-xs">
                {management.description}
              </span>
            </Command.Item>
          ) : null}
          {showEmpty && (
            <EmptyState
              data-testid="page-palette-empty"
              title="No page matches"
              description="Try another word from the name, keywords, a figure, section, or description."
            />
          )}
          {searching ? (
            (emptyTopic || management ? [] : pages).map(entry =>
              item(entry, entry.page.id === leadId)
            )
          ) : (
            <>
              {questionPage !== undefined || alertPage !== undefined ? (
                <Command.Group
                  heading="Try asking"
                  data-testid="page-palette-try-asking"
                  className={GROUP_CLASS}
                >
                  {questionPage !== undefined
                    ? tryQuestion(
                        'question',
                        `Show me ${questionPage.page.title}`
                      )
                    : null}
                  {alertPage !== undefined
                    ? tryQuestion(
                        'alert',
                        `Alert me when ${alertPage.page.title} changes`
                      )
                    : null}
                </Command.Group>
              ) : null}
              {storedGroup(
                'starred',
                'Starred',
                starredPages ?? NO_STORED_PAGES
              )}
              {storedGroup('recent', 'Recent', recentPages ?? NO_STORED_PAGES)}
              {grouped(pages).map(section => (
                <Command.Group
                  key={section.group}
                  heading={section.group}
                  data-testid={`page-palette-group-${slugifyGroup(section.group)}`}
                  className={GROUP_CLASS}
                >
                  {section.pages.map(entry => item(entry, false))}
                </Command.Group>
              ))}
            </>
          )}
          {offerAsk ? (
            <Command.Item
              data-testid="page-palette-ask-ai"
              value={ASK_AI_VALUE}
              onSelect={() => {
                setAskedSearch(search)
                // The answer may bring the alert fallback, which takes an untouched selection.
                setSelectionTouched(false)
                setSelected('')
                input.current?.focus()
              }}
              className={ITEM_CLASS}
            >
              <span className="flex min-w-0 items-center gap-2 font-medium">
                <Icon name="sparkles" className="size-4 shrink-0" />
                <span className="truncate">Ask AI: “{search.trim()}”</span>
              </span>
              <span className="text-muted-foreground text-xs">
                Finds the reports that answer your question
              </span>
            </Command.Item>
          ) : null}
          {suggestions.map((entry, index) => item(entry, index === 0, true))}
          {visibleActions.map((action, index) => (
            <Command.Item
              key={action.id}
              data-testid={`page-palette-action-${action.id}`}
              aria-label={`${action.label}, action`}
              value={actionCommandValue(action.id)}
              onSelect={() => {
                onAction(action.id)
                onOpenChange(false)
              }}
              className={ITEM_CLASS}
            >
              {index === 0 ? (
                <small
                  data-testid="page-palette-actions"
                  // The accessible name identifies this row as an action.
                  aria-hidden="true"
                  className="text-muted-foreground text-xs font-medium"
                >
                  Actions
                </small>
              ) : null}
              <span className="block min-w-0 truncate font-medium">
                {action.label}
              </span>
            </Command.Item>
          ))}
          {showMerchants &&
            matches.flatMap((merchant, index) => {
              const rows: JSX.Element[] = []
              // The first merchant row carries the section caption. A caption
              // of its own would be an item cmdk sorts or lets the reader pick.
              const caption = (): JSX.Element | null =>
                index === 0 && rows.length === 0 ? (
                  <small
                    data-testid="page-palette-merchants"
                    // Each row names its own action and merchant. Read aloud,
                    // the caption would join the first row's name.
                    aria-hidden="true"
                    className="text-muted-foreground text-xs font-medium"
                  >
                    Merchants
                  </small>
                ) : null
              if (typeof merchant360Path === 'string') {
                rows.push(
                  <Command.Item
                    key={`360-${merchant.orgId}`}
                    data-testid={`page-palette-merchant-360-${merchant.orgId}`}
                    value={merchantCommandValue(merchant.orgId, '360')}
                    onSelect={() => {
                      onNavigate(
                        merchantReportHref(merchant360Path, '', merchant.orgId)
                      )
                      onOpenChange(false)
                    }}
                    className={ITEM_CLASS}
                  >
                    {caption()}
                    <span className="block min-w-0 truncate font-medium">
                      Open Merchant 360
                    </span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {merchantLabel(merchant)} · {merchant.orgId}
                    </span>
                  </Command.Item>
                )
              }
              if (report !== undefined && report !== null) {
                rows.push(
                  <Command.Item
                    key={`filter-${merchant.orgId}`}
                    data-testid={`page-palette-merchant-filter-${merchant.orgId}`}
                    value={merchantCommandValue(merchant.orgId, 'filter')}
                    onSelect={() => {
                      onNavigate(
                        merchantReportHref(
                          report.path,
                          report.search,
                          merchant.orgId
                        )
                      )
                      onOpenChange(false)
                    }}
                    className={ITEM_CLASS}
                  >
                    {caption()}
                    <span className="block min-w-0 truncate font-medium">
                      Filter this report to {merchantLabel(merchant)}
                    </span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {merchant.orgId}
                    </span>
                  </Command.Item>
                )
              }
              return rows
            })}
          {alertNonePage !== undefined ? (
            <Command.Item
              data-testid="page-palette-alert-none"
              value={ALERT_NONE_VALUE}
              onSelect={() => {
                onNavigate(alertNonePage.path)
                onOpenChange(false)
              }}
              className={ITEM_CLASS}
            >
              No report with alerts matches · Open My alerts
            </Command.Item>
          ) : null}
          {searching && !management && (
            <Command.Item
              data-testid="page-palette-see-all"
              value={SEE_ALL_VALUE}
              onSelect={() => {
                onNavigate('/' + defaultStringifySearch({ q: search }))
                onOpenChange(false)
              }}
              className={ITEM_CLASS}
            >
              See all results for “{search}”
            </Command.Item>
          )}
        </Command.List>
      </Command>
    </Panel>
  )
}
